import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Clapperboard } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { ContentCard } from "@/components/ContentCard";
import { useAuth } from "@/context/AuthContext";

export default function Movies() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [genres, setGenres] = useState([]);
  const [genre, setGenre] = useState(params.get("genre") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { kids } = useAuth();

  useEffect(() => {
    api.get("/content/movies/genres").then(({ data }) => setGenres(data)).catch(() => setGenres([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = genre ? { genre, kids, limit: 80 } : { movie: true, kids, limit: 80 };
    api.get("/channels", { params: q })
      .then(({ data }) => setItems(data.items))
      .finally(() => setLoading(false));
  }, [genre, kids]);

  const pick = (g) => {
    setGenre(g);
    if (g) setParams({ genre: g }); else setParams({});
  };

  return (
    <div className="pt-24 px-4 sm:px-8 lg:px-12 pb-24 min-h-screen" data-testid="movies-page">
      <div className="flex items-center gap-3 mb-2">
        <Clapperboard className="text-brand" size={28} />
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">{t("movies.title")}</h1>
      </div>
      <p className="text-zinc-400 mb-6">{t("movies.subtitle")}</p>

      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-4 mb-6" data-testid="genre-filters">
        <button onClick={() => pick("")} data-testid="genre-all"
          className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${genre === "" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
          🎬 {t("movies.onNow")}
        </button>
        {genres.map((g) => (
          <button key={g.value} onClick={() => pick(g.value)} data-testid={`genre-${g.value}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${genre === g.value ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
            <span>{g.emoji}</span> {g.label}<span className="text-xs opacity-60">{g.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" size={36} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-zinc-500">{t("movies.empty")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((it) => <ContentCard key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
}
